import { Link } from 'react-router-dom';
import { useSEO, SEOInjection } from '@/hooks/useSEO';
import { aboutData } from '@/mocks/homeData';
import { aboutImages } from '@/mocks/imageUrls';
import { SectionTag, SectionTitle } from '@/components/base/Card';
import ContactForm from './components/ContactForm';

export default function AboutPage() {
  const canonicalUrl = `${window.location.origin}/about`;

  useSEO({
    title: '关于我们 - 豪门国际 | 豪门国际官网',
    description: '豪门国际是全球领先的高端资源整合平台，创立于2010年，总部位于香港。了解豪门国际的品牌故事、核心价值观、行业声誉与联系方式。',
    keywords: '豪门国际,关于豪门国际,豪门国际品牌,豪门国际联系方式,高端资源平台,精英社交',
    canonical: canonicalUrl,
    ogTitle: '关于我们 - 豪门国际',
    ogDescription: '豪门国际是全球领先的高端资源整合平台，致力于为全球高净值人群与精英阶层提供顶级的国际资源对接服务。',
    ogUrl: canonicalUrl,
  });

  return (
    <>
      <SEOInjection jsonLd={{
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于豪门国际',
        description: '豪门国际是全球领先的高端资源整合平台，致力于为全球高净值人群与精英阶层提供顶级的国际资源对接、高端商务合作与精英社交服务。',
        url: canonicalUrl,
      }} />

      <main>
        <section className="relative pt-20 md:pt-24 pb-14 md:pb-20 bg-foreground-900 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={aboutImages.brandHero}
              alt="豪门国际总部"
              className="w-full h-full object-cover object-top opacity-25"
              title="豪门国际 - 品牌介绍"
            />
          </div>
          <div className="relative z-10 container-custom">
            <div className="max-w-3xl">
              <p className="font-label text-sm tracking-[0.3em] text-primary-300 mb-4">ABOUT US</p>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 mb-6">
                关于{aboutData.brandName}
              </h1>
              <p className="text-base md:text-lg text-background-50/70 leading-relaxed">
                {aboutData.brandIntro}
              </p>
            </div>
          </div>
        </section>

        <section className="section-padding bg-background-50">
          <div className="container-custom">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <SectionTag>使命与愿景</SectionTag>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="p-8 rounded-lg bg-background-100 border border-background-200/60">
                  <div className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 mb-4">
                    <i className="ri-flag-line text-lg" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">我们的使命</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{aboutData.mission}</p>
                </div>

                <div className="p-8 rounded-lg bg-background-100 border border-background-200/60">
                  <div className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 mb-4">
                    <i className="ri-eye-line text-lg" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">我们的愿景</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{aboutData.vision}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="section-padding bg-background-100">
          <div className="container-custom">
            <div className="text-center mb-14">
              <SectionTag>核心价值观</SectionTag>
              <SectionTitle>驱动我们前行的力量</SectionTitle>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {aboutData.values.map((val) => (
                <div
                  key={val.title}
                  className="p-6 md:p-8 rounded-lg bg-background-50 border border-background-200/60 hover:border-primary-200/60 transition-all duration-300 text-center"
                >
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">{val.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{val.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section-padding bg-background-50">
          <div className="container-custom">
            <div className="text-center mb-14">
              <SectionTag>品牌声誉</SectionTag>
              <SectionTitle>行业认可与荣誉</SectionTitle>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 max-w-4xl mx-auto">
              {aboutData.reputation.map((item) => (
                <div
                  key={`${item.title}-${item.year}`}
                  className="p-6 rounded-lg bg-background-50 border border-background-200/60 hover:border-primary-200/60 transition-all duration-300"
                >
                  <div className="flex items-center space-x-2 mb-3">
                    <i className="ri-award-line text-primary-500 text-lg" />
                    <span className="text-xs font-medium text-primary-600">{item.year}</span>
                  </div>
                  <h4 className="font-heading text-base font-semibold text-foreground-900 mb-2">{item.title}</h4>
                  <p className="text-xs text-foreground-500">{item.org}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="section-padding bg-background-100">
          <div className="container-custom">
            <div className="text-center mb-14">
              <SectionTag>联系我们</SectionTag>
              <SectionTitle>期待与您相遇</SectionTitle>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-12 max-w-5xl mx-auto">
              <div className="lg:col-span-2 space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-md bg-primary-100 text-primary-600">
                    <i className="ri-map-pin-line text-lg" />
                  </div>
                  <div>
                    <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1">公司地址</h4>
                    <p className="text-sm text-foreground-600 leading-relaxed">{aboutData.contact.address}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-md bg-primary-100 text-primary-600">
                    <i className="ri-phone-line text-lg" />
                  </div>
                  <div>
                    <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1">联系电话</h4>
                    <p className="text-sm text-foreground-600">{aboutData.contact.phone}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-md bg-primary-100 text-primary-600">
                    <i className="ri-mail-line text-lg" />
                  </div>
                  <div>
                    <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h4>
                    <p className="text-sm text-foreground-600">{aboutData.contact.email}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-md bg-primary-100 text-primary-600">
                    <i className="ri-wechat-line text-lg" />
                  </div>
                  <div>
                    <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1">官方微信</h4>
                    <p className="text-sm text-foreground-600">{aboutData.contact.wechat}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-md bg-primary-100 text-primary-600">
                    <i className="ri-time-line text-lg" />
                  </div>
                  <div>
                    <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1">工作时间</h4>
                    <p className="text-sm text-foreground-600">{aboutData.contact.workingHours}</p>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-3">
                <ContactForm />
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}